import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { KeyValuePipe } from '@angular/common';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { DatePicker } from 'primeng/datepicker';

_('error.required');
_('error.minlength');
_('error.maxlength');
_('error.min');
_('error.max');
_('error.pattern');
_('error.not_matching');
_('error.email');
_('error.lostupdate');

@Component({
  selector: 'app-input-with-error',
  templateUrl: './input-with-error.component.html',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    DatePicker,
    TranslatePipe,
    TranslatePipe,
    KeyValuePipe,
    SelectModule,
    MultiSelectModule,
  ],
})
export class InputWithErrorComponent implements OnInit {
  private translateService = inject(TranslateService);

  @Input() control!: FormControl<unknown>;

  @Input() invalid = false;

  @Input() inputType:
    'input' | 'date' | 'textarea' | 'checkbox' | 'select' | 'multi-select' =
    'input';

  @Input() selectValues: unknown[] = [];

  @Input() selectNames?: string[];

  @Input() type: 'text' | 'password' | 'email' = 'text';

  @Input() description?: string = '';

  @Input() selectDefaultText?: string;

  @Input() selectDefaultIsDisabled?: boolean = true;

  @Input() helpText?: string;

  @Input() prependIcon?: string;

  @Input() rows = '4';

  @Input() translateSelect = true;

  @Output() keyUpEvent = new EventEmitter<KeyboardEvent>();

  protected listOfErrors = signal<Record<string, unknown>>(
    {} as Record<string, unknown>,
  );
  protected errors = {} as Record<string, unknown>;
  protected checkboxId = `checkbox-${Math.random().toString(36).substr(2, 9)}`;

  get selectOptions(): { label: string; value: unknown }[] {
    const options = this.selectValues.map((value, index) => ({
      label: this.getSelectLabel(index),
      value,
    }));

    if (this.selectDefaultText && !this.selectDefaultIsDisabled) {
      options.unshift({
        label: this.getTranslatedText(this.selectDefaultText),
        value: null,
      });
    }

    return options;
  }

  get selectPlaceholder(): string | undefined {
    if (!this.selectDefaultText) {
      return this.translateService.instant('select');
    }
    if (!this.selectDefaultIsDisabled) {
      return undefined;
    }
    return this.getTranslatedText(this.selectDefaultText);
  }

  ngOnInit() {
    this.control.statusChanges.subscribe(() => {
      this.updateListOfErrors();
    });
  }

  keyUp($event: KeyboardEvent): void {
    this.keyUpEvent.emit($event);
  }

  private getSelectLabel(index: number): string {
    const label = this.selectNames?.[index] ?? String(this.selectValues[index]);
    return this.getTranslatedText(label);
  }

  private getTranslatedText(value: string): string {
    if (!value) {
      return '';
    }
    return this.translateSelect ? this.translateService.instant(value) : value;
  }

  private updateListOfErrors() {
    const errors = this.control.errors || {};
    this.listOfErrors.set(
      Object.keys(errors).reduce(
        (acc, error) => ({
          ...acc,
          [`error.${error}`]: errors[error],
        }),
        {} as Record<string, unknown>,
      ),
    );
  }
}
